import { useEffect, useState } from 'react';
// src/pages/AgreementPage.jsx
import { useLocation, useNavigate } from "react-router-dom";
import apiService from '../services/apiService';

import { COMPANY } from "../constants/company";

const InfoNote = ({ children }) => (
  <div className="rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-900 px-4 py-3 text-sm border border-indigo-200/60 backdrop-blur-sm my-3">
    <div className="flex items-start gap-2">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 shrink-0 text-indigo-500 mt-0.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{children}</span>
    </div>
  </div>
);

const H = ({ children, className = "" }) => (
  <h3 className={`mt-6 text-base font-bold text-slate-900 flex items-center gap-2 ${className}`}>
    <span className="inline-block w-1 h-5 rounded bg-gradient-to-b from-indigo-500 to-purple-500"></span>
    {children}
  </h3>
);

// Best-effort capture of the signer's public IP + location for the consent
// audit record. Every step is wrapped so a failure (blocked permission,
// offline lookup service) never blocks the agreement submission — the server
// falls back to the request IP when these come back empty.
async function captureSignerMeta() {
  const meta = { ipAddress: "", location: "", lat: null, lng: null };

  // Public IP — ipify is a stable, key-less endpoint.
  try {
    const r = await fetch("https://api.ipify.org?format=json");
    const j = await r.json();
    meta.ipAddress = j?.ip || "";
  } catch (_) {
    /* ignore — server will fall back to the request IP */
  }

  // Precise coordinates (needs user permission) — short timeout, best effort.
  try {
    const pos = await new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("no geolocation"));
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 60000,
      });
    });
    meta.lat = pos.coords.latitude;
    meta.lng = pos.coords.longitude;

    // Reverse-geocode to a human-readable place (key-less endpoint).
    try {
      const g = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${meta.lat}&longitude=${meta.lng}&localityLanguage=en`
      );
      const gj = await g.json();
      meta.location = [gj.city, gj.principalSubdivision, gj.countryName]
        .filter(Boolean)
        .join(", ");
    } catch (_) {
      /* coords still sent even if reverse-geocode fails */
    }
  } catch (_) {
    /* geolocation denied/unavailable — IP-only audit record */
  }

  return meta;
}

export default function AgreementPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Extract query params from URL
  const searchParams = new URLSearchParams(location.search);
  const queryName = searchParams.get("name") || "";
  const queryEmail = searchParams.get("email") || "";
  const queryMobile = searchParams.get("mobile") || "";

  // Form state
  const [fullName, setFullName] = useState(queryName);
  const [email, setEmail] = useState(queryEmail);
  const [mobile, setMobile] = useState(queryMobile);
  const [accepted, setAccepted] = useState(true);

  // --- Email OTP state ---
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [loading, setLoading] = useState(false);

  // Cooldown timer
  useEffect(() => {
    if (!otpSent || otpCountdown <= 0) return;
    const t = setInterval(
      () => setOtpCountdown((s) => Math.max(0, s - 1)),
      1000
    );
    return () => clearInterval(t);
  }, [otpSent, otpCountdown]);

  // ✅ Send OTP
  const handleSendOtp = async () => {
    if (!email) return alert("Please enter your email first.");
    try {
      setSending(true);
      const res = await apiService.sendOtp(email);
      if (res.ok) {
        setOtpSent(true);
        setOtpCountdown(30);
        setOtp("");
        alert("✅ OTP sent successfully!");
      } else {
        alert(res.message || "Failed to send OTP.");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Server error while sending OTP.");
    } finally {
      setSending(false);
    }
  };

  // ✅ Verify OTP
  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return alert("Please enter a 6-digit OTP.");
    try {
      setVerifying(true);
      const res = await apiService.verifyOtp(email, otp);
      if (res.ok) {
        setOtpVerified(true);
        alert("✅ Email verified successfully!");
      } else {
        alert("❌ Invalid or expired OTP.");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Failed to verify OTP.");
    } finally {
      setVerifying(false);
    }
  };

  // ✅ Submit Agreement (send agreement email)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) return alert("You must accept the Terms & Conditions first.");
    if (!otpVerified) return alert("Please verify your email first.");

    try {
      setLoading(true);
      const meta = await captureSignerMeta();
      const res = await apiService.sendAgreement({ email, ...meta });
      if (res.ok) {
        alert("✅ Agreement PDF sent successfully to your email!");
        navigate("/thank-you", { state: { fullName, email, mobile } });
      } else {
        alert("❌ Failed to send agreement email.");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Server error while sending agreement email.");
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = accepted && otpVerified;

  return (
    <div className="glass-bg-light py-10 px-4">
      <div className="relative z-10 mx-auto w-full sm:max-w-3xl">
        <div className="glass-card rounded-[2rem] p-6 sm:p-10 animate-fade-up">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shadow-xl shadow-indigo-200 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                className="h-7 w-7 text-white"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gradient">
              User Agreement
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Review your details and accept Terms &amp; Conditions.
            </p>
          </div>

          {/* Terms content - inside a scroll glass container */}
          <div className="glass-inner rounded-2xl p-5 sm:p-6 max-h-[420px] overflow-y-auto mt-4">
            <div className="prose max-w-none prose-sm text-slate-800 leading-7">
              <p className="font-semibold">Parties to these Terms and Conditions:</p>

              <p>
                <strong>i. Research Analyst:</strong> {COMPANY.NAME}, a SEBI Registered Research Analyst, holding
                Registration No. {COMPANY.SEBI_REG_NO}, with its registered office
                located at {COMPANY.ADDRESS}, hereinafter referred to as
                the "Research Analyst" or "RA".
              </p>

              <p>
                <strong>ii. Client:</strong> The individual or entity
                subscribing to or availing research services provided by the
                Research Analyst, hereinafter referred to as the "Client".
              </p>

              <H>1. Availing The Services:</H>
              <p>
                The Client hereby accepts the research services and confirms to
                avail the research services on its own discretion provided by
                the Research Analyst ("RA"), and the RA agrees to provide such
                services in accordance with the terms and conditions set forth
                underneath.
              </p>

              <H>2. Obligations on RA and Client:</H>
              <p>
                The Client and the Research Analyst shall be bound by all
                applicable regulations, rules, circulars, and amendments issued
                by SEBI, including the SEBI (Research Analysts) Regulations,
                2014 and all the other notifications of the Government, if any
                from time to time.
              </p>

              <H>3. Client Information and KYC:</H>
              <p>
                The Client is bound, upon acceptance of services, to submit all
                requisite documents as requested by the research analyst and
                help the RA to complete the KYC process.
              </p>

              <InfoNote>
                "The client hereby gives consent to the research analyst to
                fetch his KYC documents from the KYC Registration Agency (KRA)."
              </InfoNote>

              <H>4. Standard Terms of Service:</H>
              <p>
                The Client acknowledges and gives his consent to be bound by the
                terms set forth herewith, as well as any applicable amendments
                or updates provided by the Research Analyst.
              </p>

              <p className="font-semibold">The client hereby agrees:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>
                  I have read and understood the terms and conditions applicable
                  to a research analyst as defined under regulation 2(1)(u) of
                  the SEBI (Research Analyst) Regulations, 2014, including the
                  fee structure.
                </li>
                <li>
                  I am subscribing to the research services for my own benefit
                  and consumption, and any reliance placed on the research
                  report provided by the Research Analyst shall be based on my
                  own judgment and assessment of the conclusions contained in
                  the research report.
                </li>
              </ol>

              <p className="mt-4 font-semibold">I understand that:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Any investment made based on the recommendations in the research report is subject to market risk.</li>
                <li>Recommendations in the research report do not provide any assurance of returns.</li>
                <li>There is no recourse to claim any losses incurred on investments made based on the recommendations in the research report.</li>
              </ul>

              <H>Declaration by Research Analyst:</H>
              <ol className="list-decimal pl-5 space-y-1">
                <li>It is duly registered with SEBI as an RA having Registration No.: {COMPANY.SEBI_REG_NO}, Date of Registration: {COMPANY.SEBI_REG_DATE}.</li>
                <li>It has registration and qualifications required to render the services contemplated under the RA Regulations, and the same are valid and subsisting.</li>
                <li>The services provided by the RA do not conflict with or violate any provision of law, rule, regulation, contract, or other instrument to which it is a party or to which any of its property is or may be subject.</li>
                <li>The maximum fee that may be charged by the RA is {COMPANY.FEES.MAX_PER_YEAR} per annum per family of clients.</li>
                <li>The recommendations provided by the RA do not provide any assurance of returns.</li>
                <li>The RA is not engaged in any additional professional or business activities on a full-time or executive capacity that may interfere with or influence the independence of the research report and/or recommendations.</li>
              </ol>

              <H>5. Consideration And Mode of Payment:</H>
              <ol className="list-decimal pl-5 space-y-1">
                <li>The client shall duly pay the fees to the RA of the invoice raised or for any agreed amount either written/oral within 2 days.</li>
                <li>The Client agrees to make all payments via: UPI/Net banking/Payment Gateway or any other verified banking channel. Client shall not pay any fees in cash.</li>
                <li>The Client hereby agrees to pay the fees in the Bank account of Research Analyst only. The RA shall not be liable for any payment made to third party account.</li>
              </ol>

              <H>6. Risk Factors:</H>
              <p>The Client understands that the services provided by the Research Analyst involve inherent risks, and the Client agrees to bear full responsibility for any financial or other consequences arising from the use of these services.</p>
              <p>Investment in the market is subject to market risk, and is also subject to the following:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Trading in equities, derivatives, and other securities are subject to market risks and there is no assurance or guarantee of returns.</li>
                <li>Past performance does not indicate future performance.</li>
                <li>Research recommendations may not always be profitable, as actual market movements may differ from anticipated trends.</li>
                <li>The Research Analyst is not responsible or liable for any losses resulting from research recommendations.</li>
                <li>Investment in securities market are subject to market risks. Read all the related documents carefully before investing.</li>
                <li>Registration granted by SEBI, membership of BASL, and certification from NISM do not guarantees the performance of the intermediary or provide any assurance of returns to investors.</li>
              </ul>

              <H>7. Conflict of Interest:</H>
              <p>The RA shall adhere to applicable regulations/circulars/directions specified by SEBI from time to time in relation to disclosure and mitigation of any actual or potential conflict of interest. Some disclosures are as follows:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>The Research Analyst or any of its officer/employee does not trade in securities which are subject matter of recommendation.</li>
                <li>There are no actual or potential conflicts of interest arising from any connection to or association with any issuer of products/securities. Such conflict shall be disclosed to the client as and when they arise.</li>
                <li>Research Analyst or its employee or its associates have not received any compensation from the company which is subject matter of recommendation.</li>
                <li>Client is advised to refer the detailed disclosure provided on our website.</li>
              </ul>

              <H>8. Termination of Service and Refund of Fees:</H>
              <p>The Agreement may be terminated by the client if the RA fails to provide research recommendations. However, the client cannot terminate solely based on not achieving desired returns or incurring losses, or if client discontinues the services without a valid reason, or in case of force majeure.</p>
              <p>The RA may suspend or terminate services on account of suspension/cancellation of registration by SEBI and shall refund the residual amount to the client.</p>
              <p>In case of suspension for &gt; 60 days or cancellation of RA registration, RA shall refund fees on a pro-rata basis for the period from effective date of cancellation/suspension to end of subscription period.</p>

              <H>9. Grievance Redressal:</H>
              <p>For grievances related to non-receipt/missing content/deficiencies, the Client may raise a grievance. The RA will ensure redressal within 7 days.</p>
              <p className="font-semibold">Procedure:</p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Contact RA: <strong>Contact No.: {COMPANY.CONTACT.PHONE}</strong> | <strong>Mail ID: {COMPANY.CONTACT.EMAIL}</strong></li>
                <li>If unsatisfied, lodge complaint at SEBI SCORES: {COMPANY.LINKS.SCORES}</li>
                <li>Consider ODR via SMART ODR Portal: {COMPANY.LINKS.SMART_ODR}</li>
              </ol>
              <p className="mt-2 text-slate-700"><strong>DISCLAIMER:</strong> The client is strictly required to follow the procedure as mentioned above, otherwise the RA shall not be liable for delay in resolution of the grievance.</p>

              <H>10. Most Important Terms and Conditions:</H>
              <ul className="list-disc pl-5 space-y-1">
                <li>These terms/consents are for research services only; RA cannot execute trades on behalf of client.</li>
                <li>Fees subject to SEBI/RAASB maximums (for Individual/HUF). RA may charge in advance up to the allowed period (presently one year). In pre-mature termination, only proportionate unexpired period is refundable.</li>
                <li>Fees via cheque/online/UPI etc.; cash not allowed. CeFCoM (BSE) is optional.</li>
                <li>RA must disclose/mitigate conflicts per SEBI/RAASB; RA will promptly inform client of any conflict affecting services.</li>
                <li>No assured/guaranteed/fixed return schemes are permitted.</li>
                <li>RA cannot guarantee returns/profits/accuracy; opinions are based on data/assumptions as of report date.</li>
                <li>Investments based on recommendations are subject to market risks; no assurance of returns; no recourse for losses.</li>
                <li>SEBI registration/RAASB enlistment/NISM certification do not guarantee performance or returns.</li>
                <li>Grievances: first contact RA ({COMPANY.CONTACT.PHONE} / {COMPANY.CONTACT.EMAIL}), then SCORES, optionally SMART ODR.</li>
                <li>Keep contact details updated with RA at all times.</li>
                <li>RA will never ask for login credentials/OTPs of trading/demat/bank. Never share such information.</li>
              </ul>

              <H>11. Optional Centralised Fee Collection Mechanism:</H>
              <p>CeFCoM for fee payments is optional. The RA has not opted for it presently; once registered, it will be available for the client.</p>

              <H>12. Confidentiality:</H>
              <p>Client shall not share any confidential information with third party without prior consent from the RA which has come to its knowledge.</p>

              <H>13. Dispute:</H>
              <p>No suit/prosecution/legal proceeding shall lie against the RA for any damage caused or likely to be caused by anything done in good faith under the SEBI (Research Analyst) Regulations, 2014. Disputes shall be resolved via arbitration/other mutually agreed methods per applicable guidelines.</p>

              <H>14. Severability:</H>
              <p>If any provision is found invalid/illegal/unenforceable, the remaining provisions will remain in full effect, provided the essential purpose is not undermined.</p>

              <H>15. Force majeure:</H>
              <p>Neither party shall be liable for failure/delay in performance due to circumstances beyond reasonable control, including natural disasters, government actions, or other unforeseen events.</p>

              <InfoNote>
                The client agrees to abide with the terms and conditions of the research services.
              </InfoNote>
            </div>
          </div>

          {/* Confirmation/Footer form */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <h2 className="section-title text-base">Your Details</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Full Name <span className="text-pink-500">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="glass-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  Email <span className="text-pink-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="glass-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Mobile Number <span className="text-pink-500">*</span>
              </label>
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                pattern="[6-9][0-9]{9}"
                inputMode="numeric"
                required
                className="glass-input"
              />
            </div>

            {/* OTP block */}
            <div className="rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 to-purple-50/40 p-5 backdrop-blur-sm">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Email Verification <span className="text-pink-500">*</span>
                {otpVerified && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs font-medium ring-1 ring-emerald-200">
                    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Verified
                  </span>
                )}
              </label>

              <input
                type="text"
                placeholder="Enter 6-digit code"
                inputMode="numeric"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="glass-input tracking-[0.5em] text-center font-semibold text-lg"
              />

              <p className="mt-2 text-xs leading-relaxed text-slate-600">
                To verify your email address, we&apos;ll send you a 6-digit verification code.
              </p>

              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:gap-3">
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sending || otpCountdown > 0}
                  className={`h-11 w-full sm:w-auto sm:flex-1 rounded-xl px-4 text-sm font-semibold transition-all ${
                    sending || otpCountdown > 0
                      ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                      : "bg-white/80 text-indigo-700 border border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 hover:shadow-md"
                  }`}
                >
                  {otpCountdown > 0
                    ? `Resend in ${otpCountdown}s`
                    : sending
                    ? "Sending..."
                    : "Send OTP"}
                </button>

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={verifying || !otp || otp.length !== 6 || otpVerified}
                  className={`h-11 w-full sm:w-auto sm:flex-1 rounded-xl px-4 text-sm font-semibold transition-all ${
                    otpVerified
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-default"
                      : verifying || !otp || otp.length !== 6
                      ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                      : "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-lg shadow-emerald-200 hover:shadow-xl"
                  }`}
                >
                  {otpVerified
                    ? "✓ Verified"
                    : verifying
                    ? "Verifying..."
                    : "Verify"}
                </button>
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm cursor-pointer p-4 rounded-xl bg-white/60 border border-slate-200 hover:border-indigo-300 transition-all">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
              <span className="text-slate-700">
                I accept the{" "}
                <span className="font-semibold text-indigo-700">
                  Terms &amp; Conditions
                </span>{" "}
                of {COMPANY.NAME}.
              </span>
            </label>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!canSubmit || loading}
              className="btn-gradient btn-gradient-success w-full"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Accept Terms &amp; Receive PDF
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
